import { Controller } from "@hotwired/stimulus"

/**
 * Leaves a booking page that was loaded as a FULL page once its modal is gone
 * (CONEK-7918).
 *
 * The booking form / detail are modals meant to open over the calendar inside
 * its `booking_modal` Turbo Frame. Opened in their own tab (Cmd/middle-click on
 * New booking or a grid card) or from a pasted link, there is no calendar
 * behind them, so dismissing the modal in place left a blank page. This wraps
 * the frame on those full-page renders only (bookings/_standalone_exit) and
 * visits the calendar as soon as the frame holds no open dialog.
 *
 * Watching the frame rather than one button covers every in-place dismiss:
 * Cancel, the X, Esc and backdrop click (ui--modal#close removes `open` only
 * after pipeline--unsaved-changes had its chance to veto). The application
 * layout's body is data-turbo="false", so save, delete and detail → Edit are
 * native navigations there (to /calendar or the full /edit page) and never
 * reach this; should a frame ever swap or empty in place instead, an emptied
 * frame also leaves, and a swap to another open dialog lands in the same
 * mutation batch, so it never reads as "gone". Only mutations count — never the
 * state at connect — and the visit replaces the history entry, so Back can't
 * land on an empty booking page and bounce.
 */
export default class extends Controller {
  static values = { url: String }

  connect() {
    this._left = false
    this._observer = new MutationObserver(() => this._leaveIfClosed())
    this._observer.observe(this.element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["open"],
    })
  }

  disconnect() {
    this._observer?.disconnect()
    this._observer = null
  }

  _leaveIfClosed() {
    if (this._left || !this.urlValue) return
    if (this.element.querySelector("dialog[open]")) return

    this._left = true
    this._observer?.disconnect()
    window.Turbo.visit(this.urlValue, { action: "replace" })
  }
}
